import { isValue, isWild, MAX_VALUE, MIN_VALUE, WILD_KINDS, type Card, type Suit, type ValueCard, type WildKind } from "../types/card";
import type {
  BonusStackChoice,
  EngineEvent,
  GameState,
  GuessType,
  PlayerMove,
  StackIndex,
  SwapChoice,
} from "../types/game";
import { STACK_INDICES } from "../types/game";
import { blueShellTargetIds, canTargetPlayer, canUseStreak, normalizeHouseRules, streakSpendApplies } from "../engine/houseRules";
import {
  legalGuessesForStack,
  legalStackIndicesForTurn,
  legalSwapPairs,
  randomSwapChoice,
  topOf,
} from "../engine/moves";
import { matchDistance } from "../engine/matchDistance";
import { activeStarfall, chargeLevels, staircaseGain, staircaseValues } from "../engine/starfall";
import type { RNG } from "../engine/rng";

/**
 * AI opponents. Only used in single-player and local (hot-seat) multiplayer -
 * per the design doc, AI players are never injected into online games, so
 * this module has no server/network dependency.
 */

/**
 * Card-counting memory for the Hard AI. Built entirely from information that
 * is legitimately public in this game: every card that gets played is shown
 * face-up to all players during the reveal animation (design doc:
 * "every player should be able to see the animations from theirs and others
 * turns"). Deck contents are never inspected directly - that would be
 * cheating, not "probabilistic memorization."
 */
/** Serializable form of a CardMemory, for saving/resuming a local round. */
export interface CardMemorySnapshot {
  copiesPerValue: number;
  wildsSeen: number;
  playedCounts: [number, number][];
  transitions: (readonly [number, [number, number][]])[];
  /** Insane's remembered pairs, by player. Absent in saves from before Insane. */
  sequences?: [string, RememberedSequence[]][];
  /** The pair each player's last card started, which their next card may finish. */
  expecting?: [string, RememberedSequence][];
}

/**
 * A card's face as a player can tell it apart: value and suit, or the wild's
 * kind. Never the card id - two physical decks print the same face twice, and
 * telling those copies apart is exactly what a player at the table cannot do.
 */
export type CardFace = string;

export function cardFace(card: Card): CardFace {
  return isWild(card) ? `w:${card.wildKind}` : `v:${card.value}:${card.suit}`;
}

/**
 * Insane's memory of a pile it took: the two cards that were on top of it.
 * A taken pile goes onto the bottom of the deck flipped, so the card that was
 * second from the top comes back up first and the top card straight after it -
 * having played `first` again, the next card is `next`.
 */
export interface RememberedSequence {
  first: CardFace;
  next: CardFace;
}

/** A stand-in card carrying only a face, for deciding a move on a card the AI expects rather than sees. */
function cardFromFace(face: CardFace): Card {
  const [kind, a, b] = face.split(":");
  if (kind === "w") {
    const wildKind = a as WildKind;
    return { id: `expected:${face}`, kind: "wild", wildKind, wildIndex: WILD_KINDS.indexOf(wildKind) as 0 | 1 | 2 | 3, deckIndex: 0 };
  }
  return { id: `expected:${face}`, kind: "value", value: Number(a), suit: b as Suit, deckIndex: 0 };
}

export class CardMemory {
  private playedCounts = new Map<number, number>(); // value -> count seen played
  private wildsSeen = 0;
  private transitions = new Map<number, Map<number, number>>(); // topValue -> (playedValue -> count)
  private sequences = new Map<string, RememberedSequence[]>(); // playerId -> pairs off piles they took
  private expecting = new Map<string, RememberedSequence>(); // playerId -> the pair their last card started
  /** Copies of each value 1-13 in the assembled pool (2 per half deck, 4 per full deck). */
  readonly copiesPerValue: number;

  /**
   * Takes copies-per-value rather than a deck count, because deck sizes now come
   * in half-deck increments (0.5 -> 2 copies, 1 -> 4, 1.5 -> 6, ... 3 -> 12).
   * The old deck-count model silently treated a half deck as a full one and
   * mis-counted every remaining card.
   */
  constructor(copiesPerValue: number) {
    this.copiesPerValue = copiesPerValue;
  }

  observe(topValueBeforePlay: number | null, card: { kind: string; value?: number }) {
    if (card.kind === "wild") {
      this.wildsSeen++;
      return;
    }
    const v = card.value as number;
    this.playedCounts.set(v, (this.playedCounts.get(v) ?? 0) + 1);
    if (topValueBeforePlay != null) {
      if (!this.transitions.has(topValueBeforePlay)) this.transitions.set(topValueBeforePlay, new Map());
      const m = this.transitions.get(topValueBeforePlay)!;
      m.set(v, (m.get(v) ?? 0) + 1);
    }
  }

  /**
   * Remembers the top two cards of a pile `playerId` just took (`takenTopToBottom`
   * as the `stack-taken` event reveals them). A pile of one card has no pair.
   */
  rememberTakenPile(playerId: string, takenTopToBottom: readonly Card[]): void {
    if (takenTopToBottom.length < 2) return;
    const list = this.sequences.get(playerId) ?? [];
    list.push({ first: cardFace(takenTopToBottom[1]), next: cardFace(takenTopToBottom[0]) });
    this.sequences.set(playerId, list);
  }

  /**
   * A card `playerId` drew off their own deck, in the order they drew them.
   *
   * If the previous card started a remembered pair and this one finishes it,
   * the pair has come round and is forgotten. If it did not, the pair is KEPT:
   * with more than one deck in play the first card may have been another copy
   * of the same face, and the real one is still on its way. Then this card is
   * checked for starting a pair of its own.
   */
  observeOwnCard(playerId: string, card: Card): void {
    const face = cardFace(card);
    const pending = this.expecting.get(playerId);
    this.expecting.delete(playerId);
    const list = this.sequences.get(playerId);
    if (!list) return;
    if (pending && pending.next === face) {
      const i = list.findIndex((s) => s.first === pending.first && s.next === pending.next);
      if (i >= 0) list.splice(i, 1);
    }
    const starts = list.find((s) => s.first === face);
    if (starts) this.expecting.set(playerId, starts);
  }

  /** The face `playerId` expects their next card to have, or null when they have no idea. */
  expectedNextCard(playerId: string): CardFace | null {
    return this.expecting.get(playerId)?.next ?? null;
  }

  /** Every pair `playerId` is still holding in memory, oldest first. */
  rememberedSequences(playerId: string): readonly RememberedSequence[] {
    return this.sequences.get(playerId) ?? [];
  }

  /** How many copies of `value` are believed to remain unseen (in someone's deck or hidden in a stack). */
  remainingCopies(value: number): number {
    const seen = this.playedCounts.get(value) ?? 0;
    return Math.max(0, this.copiesPerValue - seen);
  }

  /** How many wild cards have been seen played this round. */
  wildsPlayed(): number {
    return this.wildsSeen;
  }

  /**
   * Plain-object snapshot of everything the AI has observed, so a saved local
   * round can restore the Hard AI's memory on resume. Without this the AI would
   * come back from a browser refresh with amnesia, which reads as the opponent
   * suddenly playing worse for no reason.
   */
  snapshot(): CardMemorySnapshot {
    return {
      copiesPerValue: this.copiesPerValue,
      wildsSeen: this.wildsSeen,
      playedCounts: [...this.playedCounts.entries()],
      transitions: [...this.transitions.entries()].map(([top, m]) => [top, [...m.entries()]] as const),
      sequences: [...this.sequences.entries()].map(([id, list]) => [id, list.map((s) => ({ ...s }))]),
      expecting: [...this.expecting.entries()].map(([id, s]) => [id, { ...s }]),
    };
  }

  static restore(snap: CardMemorySnapshot): CardMemory {
    const memory = new CardMemory(snap.copiesPerValue);
    memory.wildsSeen = snap.wildsSeen;
    memory.playedCounts = new Map(snap.playedCounts);
    memory.transitions = new Map(snap.transitions.map(([top, entries]) => [top, new Map(entries)]));
    memory.sequences = new Map((snap.sequences ?? []).map(([id, list]) => [id, list.map((s) => ({ ...s }))]));
    memory.expecting = new Map((snap.expecting ?? []).map(([id, s]) => [id, { ...s }]));
    return memory;
  }

  /** Empirical bias, in [-1, 1], for whether values above/below `topValue` have historically appeared more often. Positive favors Higher. */
  transitionBias(topValue: number): number {
    const m = this.transitions.get(topValue);
    if (!m) return 0;
    let above = 0;
    let below = 0;
    for (const [v, c] of m) {
      if (v > topValue) above += c;
      else if (v < topValue) below += c;
    }
    const total = above + below;
    if (total < 3) return 0; // not enough samples yet
    return (above - below) / total;
  }
}

export interface AIDecision {
  move: PlayerMove;
}

function randomChoice<T>(arr: T[], rng: RNG): T {
  return arr[rng.nextInt(arr.length)];
}

/** Easy: random stack, random (valid) guess. */
function decideEasy(state: GameState, rng: RNG): PlayerMove {
  const stackIndex = randomChoice(legalStackIndicesForTurn(state), rng);
  const guesses = legalGuessesForStack(state, stackIndex);
  if (guesses.length === 0) {
    return { type: "free-place", stackIndex };
  }
  return { type: "guess", stackIndex, guess: randomChoice(guesses, rng) };
}

const MATCH_BASELINE_SCORE = 0.55;

function heuristicScore(guess: GuessType, topValue: number): number {
  if (guess === "higher") return (MAX_VALUE - topValue) / (MAX_VALUE - MIN_VALUE);
  if (guess === "lower") return (topValue - MIN_VALUE) / (MAX_VALUE - MIN_VALUE);
  return MATCH_BASELINE_SCORE;
}

interface Candidate {
  stackIndex: StackIndex;
  move: PlayerMove;
  score: number;
  /** Chance this move lights a Constellations star; 0 unless the seat seeks stars. */
  stars: number;
}

/**
 * How likely a card of each value is to be the one drawn, up to a constant:
 * Hard and Insane count what has been played, Medium assumes nothing. Wilds are
 * left out, which scales every move's chance alike and so changes no ranking.
 */
type ValueWeight = (value: number) => number;

/**
 * Whether a card of `value` coming to rest on pile `stackIndex` would light a
 * star: the same diff `chargeStarfall` makes, against the meter's last look.
 */
function lightsStar(state: GameState, stackIndex: StackIndex, value: number): boolean {
  const sf = state.starfall;
  if (!sf) return false;
  const card: ValueCard = { id: "probe", kind: "value", value, suit: "spades", deckIndex: 0 };
  const stacks = state.stacks.map((stack, idx) => (idx === stackIndex ? { cards: [card, ...stack.cards] } : stack));
  const levels = chargeLevels(stacks);
  for (let v = MIN_VALUE; v <= MAX_VALUE; v++) {
    if (levels[v] > (sf.levels[v] ?? 0)) return true;
  }
  return staircaseGain(sf.stair ?? [], staircaseValues(stacks)) > 0;
}

/** Whether a card of `value` stays on a pile showing `top` under this call (null: a free placement). */
function staysOn(guess: GuessType | null, value: number, top: number): boolean {
  if (guess === null) return true;
  if (guess === "match") return matchDistance(value, top) <= 2;
  if (value === top) return true; // a duplicate stays
  return guess === "higher" ? value > top : value < top;
}

/** The chance a move lights a star: the weight of the values that would both stay and light one. */
function starChance(state: GameState, stackIndex: StackIndex, guess: GuessType | null, weight: ValueWeight): number {
  const top = guess === null ? 0 : (topOf(state.stacks[stackIndex]) as ValueCard).value;
  let hit = 0;
  let total = 0;
  for (let value = MIN_VALUE; value <= MAX_VALUE; value++) {
    const w = weight(value);
    total += w;
    if (w > 0 && staysOn(guess, value, top) && lightsStar(state, stackIndex, value)) hit += w;
  }
  return total > 0 ? hit / total : 0;
}

/**
 * Ranks every legal move. With `seekStars` the chance of lighting a star sorts
 * first and the difficulty's own score only breaks ties, so a seat that can
 * light one always goes for the likeliest, and one that cannot plays as usual.
 */
function rankCandidates(
  state: GameState,
  scoreGuess: (guess: GuessType, topValue: number, stackIndex: StackIndex) => number,
  seekStars?: ValueWeight,
): Candidate[] {
  const candidates: Candidate[] = [];
  const stars = (stackIndex: StackIndex, guess: GuessType | null) => (seekStars ? starChance(state, stackIndex, guess, seekStars) : 0);
  // Only the piles this turn may target — house rules can narrow that to the
  // tallest, or shut two of them behind a streak, and a candidate the engine
  // would refuse is worse than no candidate at all.
  for (const stackIndex of legalStackIndicesForTurn(state)) {
    const guesses = legalGuessesForStack(state, stackIndex);
    if (guesses.length === 0) {
      // free placement is guaranteed-safe
      candidates.push({ stackIndex, move: { type: "free-place", stackIndex }, score: 1.0, stars: stars(stackIndex, null) });
      continue;
    }
    const top = topOf(state.stacks[stackIndex]) as ValueCard;
    for (const guess of guesses) {
      candidates.push({
        stackIndex,
        move: { type: "guess", stackIndex, guess },
        score: scoreGuess(guess, top.value, stackIndex),
        stars: stars(stackIndex, guess),
      });
    }
  }
  candidates.sort((a, b) => b.stars - a.stars || b.score - a.score);
  return candidates;
}

/** Medium: statistically-favorable guess per stack (extreme top values favor Higher/Lower, mid-range favors Match). Always prefers a free placement when one is available. */
function decideMedium(state: GameState, seekStars?: ValueWeight): PlayerMove {
  const ranked = rankCandidates(state, (guess, topValue) => heuristicScore(guess, topValue), seekStars);
  return ranked[0].move;
}

/** Hard: Medium's heuristic, blended with empirical card-counting bias built from publicly-played cards. */
function decideHard(state: GameState, memory: CardMemory, seekStars?: ValueWeight): PlayerMove {
  const ranked = rankCandidates(state, (guess, topValue) => {
    const base = heuristicScore(guess, topValue);
    if (guess === "match") return base;
    const bias = memory.transitionBias(topValue); // positive favors higher, negative favors lower
    const direction = guess === "higher" ? bias : -bias;
    return base * 0.7 + ((direction + 1) / 2) * 0.3;
  }, seekStars);
  return ranked[0].move;
}

/**
 * How good playing the peeked card `card` this way would be. Higher is better;
 * negative loses the pile. Only ever asked about a VALUE card on a guess pile.
 *
 * The order encodes what each outcome is worth to a player trying to empty
 * their deck: a Five Out sheds five cards, an off-by-one Match three, a chain at
 * least two; a plain correct guess sheds one, as does an off-by-two Match; a
 * duplicate sheds one but plays on blind, so it ranks below all of them.
 */
function peekGuessScore(card: ValueCard, guess: GuessType, top: number): number {
  if (guess === "match") {
    const off = matchDistance(card.value, top);
    if (off === 0) return 10; // Five Out
    if (off === 1) return 6;
    if (off === 2) return 3;
    return -10;
  }
  if (card.value === top) return 1; // a duplicate: stays, but the follow-on is unknown
  const correct = guess === "higher" ? card.value > top : card.value < top;
  if (!correct) return -10;
  return Math.abs(card.value - top) === 1 ? 5 : 3; // a +/-1 starts a chain (Higher/Lower never wrap)
}

/** Score for a free placement of a peeked value card: it always stays, and sheds exactly one. */
const PEEK_FREE_PLACE_SCORE = 3;

/**
 * Medium and Hard, when a Peek is showing them their next card: play it where
 * it does the most good.
 *
 * The card is `deck[0]`, and reading it is not cheating the way reading the
 * rest of the deck would be — it is exactly what the Peek shows the player, on
 * screen, and online it is published to them (`peekCard`). A human in the same
 * seat is making this same decision with the same information.
 *
 * Ties go to the taller pile and then the lower index: shedding cards onto a
 * tall pile is what keeps the next player from taking it cheaply. A wild has
 * no value, so there is nothing to score — every legal pile ties and that rule
 * alone decides, which for a Smash is the whole point (the taller the pile, the
 * more it destroys) and for any other kind is as good a choice as any.
 *
 * With `seekStars` the card is known, so its chance of lighting a star is all
 * or nothing: a placement where it stays and lights one outranks every other.
 */
const PEEK_STAR_BONUS = 100;

function decideWithPeek(state: GameState, card: Card, seekStars = false): PlayerMove {
  let best: { move: PlayerMove; score: number; size: number; stackIndex: StackIndex } | null = null;
  for (const stackIndex of legalStackIndicesForTurn(state)) {
    const size = state.stacks[stackIndex].cards.length;
    const guesses = legalGuessesForStack(state, stackIndex);
    const options: { move: PlayerMove; score: number }[] = [];
    const lights = seekStars && !isWild(card) && lightsStar(state, stackIndex, card.value);
    if (guesses.length === 0) {
      options.push({ move: { type: "free-place", stackIndex }, score: isWild(card) ? 0 : PEEK_FREE_PLACE_SCORE + (lights ? PEEK_STAR_BONUS : 0) });
    } else if (isWild(card)) {
      // A wild is never evaluated, so which guess carries it does not matter.
      options.push({ move: { type: "guess", stackIndex, guess: guesses[0] }, score: 0 });
    } else {
      const top = (topOf(state.stacks[stackIndex]) as ValueCard).value;
      for (const guess of guesses) {
        const bonus = lights && staysOn(guess, card.value, top) ? PEEK_STAR_BONUS : 0;
        options.push({ move: { type: "guess", stackIndex, guess }, score: peekGuessScore(card, guess, top) + bonus });
      }
    }
    // Within a pile the first of equals wins, in `legalGuessesForStack` order.
    for (const option of options) {
      const better =
        best === null ||
        option.score > best.score ||
        (option.score === best.score && size > best.size) ||
        (option.score === best.score && size === best.size && stackIndex < best.stackIndex);
      if (better) best = { ...option, size, stackIndex };
    }
  }
  if (!best) throw new Error("no legal move to make");
  return best.move;
}

/**
 * Insane: Hard, plus a memory of every pile it has taken. When the card it just
 * played started a remembered pair, it plays its next card as if a Peek were
 * showing it the other half - right whenever that copy really was the one.
 *
 * It never reads its deck to do so. The pair comes from the `stack-taken`
 * reveal every player at the table watched, and the order a taken pile comes
 * back in is the rule, not a secret.
 */
function decideInsane(state: GameState, memory: CardMemory, seekStars?: ValueWeight): PlayerMove {
  const player = state.players[state.currentPlayerIndex];
  const expected = memory.expectedNextCard(player.id);
  if (expected) return decideWithPeek(state, cardFromFace(expected), seekStars !== undefined);
  return decideHard(state, memory, seekStars);
}

/**
 * A star-seeking seat (`aiSeeksStars`) at a table playing Constellations makes
 * whichever move is likeliest to light a star, when any can; Easy stays random.
 * Only the card coming to rest is judged, not a chain or bonus laid after it.
 */
export function decideMove(state: GameState, rng: RNG, memory?: CardMemory): PlayerMove {
  const player = state.players[state.currentPlayerIndex];
  const difficulty = player.aiDifficulty ?? "medium";
  if (difficulty === "easy") return decideEasy(state, rng);
  const seeking = player.aiSeeksStars === true && activeStarfall(state) !== null;
  if (player.peeking && player.deck.length > 0) return decideWithPeek(state, player.deck[0], seeking);
  const counted: ValueWeight = memory ? (value) => memory.remainingCopies(value) : () => 1;
  const seekStars = seeking ? counted : undefined;
  if (difficulty === "insane" && memory) return decideInsane(state, memory, seekStars);
  if (difficulty === "hard" && memory) return decideHard(state, memory, seekStars);
  return decideMedium(state, seekStars);
}

/**
 * Whether a CPU seat spends its streak before taking its turn, and on whom.
 *
 * Returns the id to aim at, `BLUE_SHELL_AIMS_ITSELF` when the table is playing
 * Blue Shell (the engine picks, and the caller passes no target at all), or null
 * for "not this turn".
 *
 * It spends whenever it legally can, at every difficulty — never holding one
 * back for a better moment. A streak is one shot earned over five turns, and a
 * CPU sitting on a lit deck is a threat the table can see and never has to
 * answer; spending it on sight is also the only habit a human can read off the
 * opponents and copy.
 *
 * Aimed play picks a legal opponent AT RANDOM. Targeting is the one part of this
 * rule with no right answer — the fire costs the spender nothing either way, so
 * "closest to going out" is a preference rather than a deduction, and a table of
 * CPUs all holding it would gang up on the same seat every single time. Random
 * spreads the marks and leaves the interesting decision where it belongs, with
 * the human. A player already carrying the maximum is skipped, since another
 * mark on them would be the fire spent for nothing, and so is a player who is
 * out — the last lap puts one of those on every table.
 */
export const BLUE_SHELL_AIMS_ITSELF = "__blue-shell__";

export function decideStreakTarget(state: GameState, rng: RNG): string | null {
  const player = state.players[state.currentPlayerIndex];
  if (!player) return null;
  const rules = normalizeHouseRules(state.houseRules);
  if (!streakSpendApplies(rules, state.players.length)) return null;
  if (!canUseStreak(rules, player.streak ?? 0)) return null;
  const targets = state.streakTargets ?? [];

  if (rules.blueShell) {
    // No choice to make, but there is still a question: the spender comes out of
    // its own lead, so a sole leader has nobody to aim at, and a lead already at
    // the cap would absorb nothing.
    const aimedAt = blueShellTargetIds(
      state.players.map((p) => ({ playerId: p.id, cardsLeft: p.deck.length })),
      player.id,
    );
    if (!aimedAt.some((id) => canTargetPlayer(targets, id))) return null;
    return BLUE_SHELL_AIMS_ITSELF;
  }

  // A player who is out is skipped for the same reason a capped one is: the mark
  // has no turn to land on, and the engine refuses it outright.
  const candidates = state.players.filter(
    (p) => p.id !== player.id && p.deck.length > 0 && canTargetPlayer(targets, p.id),
  );
  if (candidates.length === 0) return null;
  return randomChoice(candidates, rng).id;
}

/** Chooses one of the two Match-off-by-one bonus stacks (excludes the origin stack; otherwise picks at random among the remaining 4). */
export function decideBonusStackChoice(state: GameState, rng: RNG): BonusStackChoice {
  const decision = state.pendingDecision;
  if (decision?.type !== "match-off-by-one-bonus") throw new Error("no pending bonus decision");
  const options = STACK_INDICES.filter((i) => i !== decision.originStackIndex);
  return { stackIndex: randomChoice(options, rng) };
}

/**
 * How likely a Higher/Lower guess on this top card is to lose the pile, in
 * [0, 1]: the share of the range on the SHORTER side of it. A 1 or a 13 cannot
 * be lost (0), a 7 is a coin flip (0.5). A wild is a free placement and cannot
 * be lost either.
 *
 * Match is left out on purpose. It is the riskier guess everywhere but the
 * extremes, so a pile's danger is set by the best guess it offers, not the worst.
 */
function topRisk(top: Card | undefined): number {
  if (!top || !isValue(top)) return 0;
  return 1 - Math.max(MAX_VALUE - top.value, top.value - MIN_VALUE) / (MAX_VALUE - MIN_VALUE);
}

/** Pile sizes and each pile's top-card risk, as they would be after swapping the tops of `a` and `b`. */
function boardAfterSwap(state: GameState, [a, b]: [StackIndex, StackIndex]): { size: number; risk: number }[] {
  const tops = state.stacks.map((st) => st.cards[0]);
  [tops[a], tops[b]] = [tops[b], tops[a]];
  return state.stacks.map((st, i) => ({ size: st.cards.length, risk: topRisk(tops[i]) }));
}

/**
 * Which pile a sensible next player takes their turn on: the safest one with a
 * card on it, and among equally safe piles the tallest (the most to protect by
 * playing on it, the most shed onto it). Null when the table is bare.
 */
function likelyNextPile(board: { size: number; risk: number }[]): number | null {
  let pick: number | null = null;
  board.forEach((pile, i) => {
    if (pile.size === 0) return;
    if (pick === null || pile.risk < board[pick].risk || (pile.risk === board[pick].risk && pile.size > board[pick].size)) {
      pick = i;
    }
  });
  return pick;
}

/**
 * Where a CPU seat makes an owed swap.
 *
 * Easy swaps at random. Medium and Hard play the swap for what it is, a move on
 * the board somebody ELSE inherits — and a swap moves only top cards, so what it
 * changes is how dangerous each pile is to guess on, never how big it is.
 *
 * Solo, there is nobody else: the next turn is the CPU's own, so it makes the
 * table as safe as it can for itself, weighting each pile's risk by its size
 * (losing a tall pile costs more). With opponents it assumes the next player
 * takes the safest pile on offer and makes THAT pile as expensive as possible —
 * a tempting pile that is tall and dangerous is the trap. Ties go to the first
 * pair in `legalSwapPairs` order.
 */
export function decideSwapChoice(state: GameState, rng: RNG): SwapChoice {
  const decision = state.pendingDecision;
  if (decision?.type !== "swap-top-cards") throw new Error("no pending swap decision");
  const player = state.players.find((p) => p.id === decision.playerId);
  if ((player?.aiDifficulty ?? "medium") === "easy") return randomSwapChoice(state, rng);

  const solo = state.players.length === 1;
  let best: { pair: [StackIndex, StackIndex]; score: number } | null = null;
  for (const pair of legalSwapPairs(state)) {
    const board = boardAfterSwap(state, pair);
    let score: number;
    if (solo) {
      score = -board.reduce((sum, pile) => sum + pile.size * pile.risk, 0);
    } else {
      const next = likelyNextPile(board);
      score = next === null ? 0 : board[next].size * board[next].risk;
    }
    if (best === null || score > best.score) best = { pair, score };
  }
  if (!best) throw new Error("there are not two piles to swap");
  return { stackIndices: best.pair };
}

/**
 * Feeds a batch of engine events into a CardMemory so the Hard AI can learn
 * from every publicly-revealed card, not just its own turns, and Insane can
 * follow its own cards through the piles it takes.
 *
 * Every event listed is a card drawn off the top of its `playerId`'s deck - a
 * comet's card is the TARGET's, and the target is who the event names - so
 * together they are each deck's cards in the order they left it.
 */
export function updateMemoryFromEvents(memory: CardMemory, events: EngineEvent[]): void {
  let lastTopByStack = new Map<number, number | null>();
  for (const ev of events) {
    if (
      ev.type === "card-played" ||
      ev.type === "chain-bonus-card" ||
      ev.type === "five-out-bonus-card" ||
      ev.type === "match-off-by-one-bonus-card" ||
      ev.type === "wild-follow-up-card" ||
      ev.type === "stack-refill-card"
    ) {
      const stackIndex = ev.stackIndex;
      const prevTop = lastTopByStack.get(stackIndex) ?? null;
      memory.observe(prevTop, ev.card as any);
      memory.observeOwnCard(ev.playerId, ev.card);
      lastTopByStack.set(stackIndex, isValue(ev.card) ? ev.card.value : null);
    }
    // Off the top of a deck like the cards above, but left out of the counting
    // as they always have been, so Hard plays exactly as it did.
    if (ev.type === "comet-card" || ev.type === "bounty-bonus-card") {
      memory.observeOwnCard(ev.playerId, ev.card);
    }
    if (ev.type === "stack-taken") {
      lastTopByStack.set(ev.stackIndex, null);
      if (ev.revealedCards) memory.rememberTakenPile(ev.playerId, ev.revealedCards);
    }
  }
}
